import { ITEM_TYPES } from '@/components/Checklists/ChecklistCreation/constants'
import { utils } from 'frontend-js-shared'
import { toNullableNumber } from '../utils'

const TEXT_LIKE_ITEM_TYPES = new Set([
  ITEM_TYPES.TEXT,
  ITEM_TYPES.IMAGE,
  ITEM_TYPES.DATE,
  ITEM_TYPES.ACCOUNT,
])

const OPTION_ITEM_TYPES = new Set([ITEM_TYPES.RADIOS, ITEM_TYPES.SELECT])

function createDefaultScoringSection({
  name = '',
  weightPercentage = null,
  id = 'default-section',
} = {}) {
  return {
    id,
    type: ITEM_TYPES.SEPARATOR,
    name,
    isDefaultSection: true,
    scoring: {
      weightPercentage,
    },
  }
}

function resolveScoringItemId(item, fallbackId = null) {
  return (
    utils.getEntityId(item) ||
    utils.getEntityId(item?.scoringItemId) ||
    fallbackId
  )
}

function getScoringItemId(item, fallbackIndex) {
  return resolveScoringItemId(item, `scoring-item-${fallbackIndex}`)
}

function hasPositiveScoreValue(value) {
  const parsedValue = toNullableNumber(value)
  return parsedValue !== null && parsedValue > 0
}

function hasExplicitScoreValue(value) {
  return toNullableNumber(value) !== null
}

function hasPositiveOptionScore(item) {
  return (Array.isArray(item?.options) ? item.options : []).some(option =>
    hasPositiveScoreValue(option?.scoringPoints)
  )
}

function hasSelectedCheckboxScoringOption(item) {
  return (Array.isArray(item?.options) ? item.options : []).some(
    option => option?.countsTowardScore
  )
}

function hasTextLikeScore(item) {
  return hasExplicitScoreValue(item?.scoring?.pointsIfAnswered)
}

function hasOptionScore(item) {
  return (Array.isArray(item?.options) ? item.options : []).some(option =>
    hasExplicitScoreValue(option?.scoringPoints)
  )
}

function hasCheckboxScore(item) {
  return (
    hasSelectedCheckboxScoringOption(item) &&
    hasExplicitScoreValue(item?.scoring?.pointsIfAllSelected)
  )
}

function hasRangeScore({ min, max, pointsIfWithinRange }) {
  return (
    toNullableNumber(min) !== null &&
    toNullableNumber(max) !== null &&
    hasExplicitScoreValue(pointsIfWithinRange)
  )
}

function normalizeQuestionScoreInput(value) {
  if (value === '' || value === null || value === undefined) {
    return 0
  }

  const parsedValue = Number(value)
  return Number.isNaN(parsedValue) ? null : Math.max(0, parsedValue)
}

function normalizeSectionWeightInput(value) {
  if (value === '' || value === null || value === undefined) {
    return 0
  }

  const parsedValue = Number(value)
  if (Number.isNaN(parsedValue)) {
    return null
  }

  return Math.min(100, Math.max(0, parsedValue))
}

function ensureWeightForSingleSection(items = []) {
  const checklistItems = Array.isArray(items) ? items : []
  const separatorIndexes = checklistItems.reduce((indexes, item, index) => {
    if (item?.type === ITEM_TYPES.SEPARATOR) {
      indexes.push(index)
    }

    return indexes
  }, [])

  if (separatorIndexes.length !== 1) {
    return checklistItems
  }

  const [separatorIndex] = separatorIndexes
  const separator = checklistItems[separatorIndex]

  if (toNullableNumber(separator?.scoring?.weightPercentage) !== null) {
    return checklistItems
  }

  return checklistItems.map((item, index) => {
    if (index !== separatorIndex) {
      return item
    }

    return {
      ...item,
      scoring: {
        ...item?.scoring,
        weightPercentage: 100,
      },
    }
  })
}

function isTextLikeQuestion(item) {
  return TEXT_LIKE_ITEM_TYPES.has(item?.type)
}

function isOptionQuestion(item) {
  return OPTION_ITEM_TYPES.has(item?.type)
}

function isCheckboxQuestion(item) {
  return item?.type === ITEM_TYPES.CHECKBOXES
}

function isNumberQuestion(item) {
  return item?.type === ITEM_TYPES.NUMBER
}

function isScaleQuestion(item) {
  return item?.type === ITEM_TYPES.SCALE
}

function isSignatureQuestion(item) {
  return item?.type === ITEM_TYPES.SIGNATURE
}

function isScoringEligibleQuestion(item) {
  if (!item) {
    return false
  }

  return item?.type !== ITEM_TYPES.SEPARATOR && !isSignatureQuestion(item)
}

export {
  createDefaultScoringSection,
  getScoringItemId,
  hasCheckboxScore,
  hasExplicitScoreValue,
  hasOptionScore,
  hasPositiveOptionScore,
  hasPositiveScoreValue,
  hasRangeScore,
  hasSelectedCheckboxScoringOption,
  hasTextLikeScore,
  isCheckboxQuestion,
  isNumberQuestion,
  isOptionQuestion,
  isScoringEligibleQuestion,
  isScaleQuestion,
  isSignatureQuestion,
  isTextLikeQuestion,
  normalizeQuestionScoreInput,
  normalizeSectionWeightInput,
  ensureWeightForSingleSection,
  OPTION_ITEM_TYPES,
  resolveScoringItemId,
  TEXT_LIKE_ITEM_TYPES
}
